// ANGULAR
import { NgModule, CUSTOM_ELEMENTS_SCHEMA, isDevMode, APP_INITIALIZER, PLATFORM_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

// NGRX
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { StoreRouterConnectingModule, RouterStateSerializer } from '@ngrx/router-store';

// STORE
import { reducers, effects, metaReducers, CustomRouterStateSerializer } from '@app/store';

// ALGOLIA
import { NgAisModule } from 'angular-instantsearch';

// ATLAS API
import { ApiModule, Configuration, ConfigurationParameters } from '@vestiaire/api-sdk-angular';

export function apiConfigFactory(
  sessionCookieService: SessionCookieService,
  localeService: LocaleService,
  platformService: PlatformService
): Configuration {
  const params: ConfigurationParameters = {
    apiKeys: {},
    defaultParams: {
      'x-currency': localeService.getCurrency(),
      'x-language': localeService.getTranslationKey(),
      'x-siteid': localeService.getSiteId()
    }
  };

  const sessionId = sessionCookieService.getSessionId();
  if (platformService.isBrowser && sessionId) {
    params.apiKeys = {
      __session: sessionId
    };
  }
  return new Configuration(params);
}

// VC
import { CoreModule } from './core/core.module';
import { AppRoutingModule } from './app-routing.module';
import { AppLocationStrategy } from './app-location-strategy.service';
import { AppComponent } from './app.component';

import { environment } from '@env/environment';
import { STATIC_BASE_PATH } from '@app/core/services/content/variables';
import { PlatformService } from './shared/services/platform/platform.service';
import { SessionCookieService } from '@app/core/cookies';
import { TransferHttpResponseModule } from './transfer-http-response.module';
import { PrebootModule } from 'preboot';

import { registerLocaleData, LocationStrategy, isPlatformBrowser, DOCUMENT } from '@angular/common';
import { LocaleService } from '@app/core/i18n';

export function getCurrentLocale(localeService: LocaleService) {
  return localeService.getLanguage();
}

// Imports Locale Data (CLDR) manually to comply with AOT requirements.
// Actually locale data should be loaded at compilation time.
import localeDe from '@angular/common/locales/de';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeIt from '@angular/common/locales/it';

registerLocaleData(localeDe, 'de');
registerLocaleData(localeEs, 'es');
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeIt, 'it');

import { Logger, ConsoleLogger, lomFormatter } from '@app/core/logging';
import { ExternalLinkResolver } from '@app/shared/guards/externalLink-guard.service';
import { IsLoggedGuard, AuthenticateExternalLinkGuard, VoucherGuard } from '@app/shared/guards';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DEFAULT_TIMEOUT, defaultTimeout, VCInterceptor } from './shared/interceptors/http-interceptor';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AlgoliaInsightService } from '@app/shared/components/catalog/services/algolia-insight.service';
import { LibraryUtilsService } from '@app/library/services/library-utils.service';

export function createLogger(platform: PlatformService): Logger {
  const logger = new Logger();
  logger.setLevel(environment.logLevel);

  if (isDevMode()) {
    logger.publishers.push(new ConsoleLogger());
  }

  if (platform.isServer) {
    logger.publishers.push(new ConsoleLogger(lomFormatter));
  }

  return logger;
}

@NgModule({
  declarations: [AppComponent],
  imports: [
    // ANGULAR PREBOOT
    BrowserModule.withServerTransition({ appId: 'vc-app' }),
    PrebootModule.withConfig({ appRoot: 'app-root' }),
    // NG UNIVERSAL
    TransferHttpResponseModule,
    HttpClientModule,
    // NGRX-STORE
    StoreModule.forRoot(reducers, { metaReducers }),
    EffectsModule.forRoot(effects),
    StoreDevtoolsModule.instrument({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production // Restrict extension to log-only mode
    }),
    StoreRouterConnectingModule.forRoot(),
    ServiceWorkerModule.register('/vc-service-workers.js', { enabled: environment.production }),
    // ATLAS API
    ApiModule,
    AppRoutingModule,
    CoreModule,
    // ALGOLIA
    NgAisModule.forRoot()
  ],
  bootstrap: [AppComponent],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: VCInterceptor, multi: true },
    { provide: DEFAULT_TIMEOUT, useValue: defaultTimeout },
    {
      provide: APP_INITIALIZER,
      useFactory: function(document: HTMLDocument, platformId: Object): Function {
        return () => {
          if (isPlatformBrowser(platformId)) {
            const styles: Element[] = Array.prototype.slice.apply(document.querySelectorAll(`style[ng-transition]`));
            // Remove ng-transition attribute to prevent Angular appInitializerFactory to remove server styles
            // before preboot complete
            styles.forEach(el => el.removeAttribute('ng-transition'));
            // After preboot complete, remove the server scripts
            document.addEventListener('PrebootComplete', () => setTimeout(() => styles.forEach(el => el.remove())), {
              passive: true,
              once: true
            });
          }
        };
      },
      deps: [DOCUMENT, PLATFORM_ID],
      multi: true
    },
    {
      provide: Configuration,
      useFactory: apiConfigFactory,
      deps: [SessionCookieService, LocaleService, PlatformService]
    },
    {
      provide: Logger,
      useFactory: createLogger,
      deps: [PlatformService]
    },
    {
      provide: STATIC_BASE_PATH,
      useValue: environment.apiStaticBaseUrl
    },
    {
      provide: RouterStateSerializer,
      useClass: CustomRouterStateSerializer
    },
    {
      provide: LocationStrategy,
      useClass: AppLocationStrategy
    },
    IsLoggedGuard,
    AuthenticateExternalLinkGuard,
    ExternalLinkResolver,
    VoucherGuard,
    AlgoliaInsightService,
    LibraryUtilsService
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule {}
